import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, SelectChangeEvent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import api from '../../api/client';

interface Agency { id: number; name: string; }

interface Summary {
  period_days: number; total_calls: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_call_type: Record<string, number>;
  by_disposition: Record<string, number>;
  avg_dispatch_seconds: number | null;
  avg_response_seconds: number | null;
  avg_turnout_seconds: number | null;
  avg_travel_seconds: number | null;
  avg_to_scene_seconds: number | null;
  avg_scene_seconds: number | null;
  calls_with_punches: number;
  calls_with_corrected_times: number;
  daily_counts: Record<string, number>;
}

interface CallTypeStat { code: string; name: string; count: number; }
interface AgencyStat { agency: string; count: number; }

const PRIORITY_LABELS: Record<string, string> = {
  '1': 'Emergency', '2': 'High', '3': 'Medium', '4': 'Low', '5': 'Info',
};

function formatDuration(seconds: number | null): string {
  if (seconds === null) return 'N/A';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export default function ReportsPage() {
  const [days, setDays] = useState(30);
  const [agencyId, setAgencyId] = useState<string>('');
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [callTypeStats, setCallTypeStats] = useState<CallTypeStat[]>([]);
  const [agencyStats, setAgencyStats] = useState<AgencyStat[]>([]);

  const load = () => {
    const params = new URLSearchParams({ days: String(days) });
    if (agencyId) params.set('agency_id', agencyId);
    api.get(`/reports/summary?${params}`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/reports/call-type-stats?days=${days}`).then((r) => setCallTypeStats(r.data)).catch(() => {});
    api.get(`/reports/agency-stats?days=${days}`).then((r) => setAgencyStats(r.data)).catch(() => {});
  };

  useEffect(() => { api.get('/agencies').then((r) => setAgencies(r.data)); }, []);
  useEffect(() => { load(); }, [days, agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Reports & Statistics</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select value={String(days)} label="Period" onChange={(e: SelectChangeEvent) => setDays(Number(e.target.value))}>
              <MenuItem value="7">7 Days</MenuItem>
              <MenuItem value="30">30 Days</MenuItem>
              <MenuItem value="90">90 Days</MenuItem>
              <MenuItem value="180">6 Months</MenuItem>
              <MenuItem value="365">1 Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Agency</InputLabel>
            <Select value={agencyId} label="Agency" onChange={(e: SelectChangeEvent) => setAgencyId(e.target.value)}>
              <MenuItem value="">All Agencies</MenuItem>
              {agencies.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button startIcon={<Refresh />} variant="outlined" onClick={load}>Refresh</Button>
        </Box>
      </Box>

      {summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card><CardContent>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>{summary.total_calls}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>Total Calls</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card><CardContent>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {formatDuration(summary.avg_dispatch_seconds)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>Avg Dispatch Time</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card><CardContent>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {formatDuration(summary.avg_response_seconds)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>Avg Total Response</Typography>
              </CardContent></Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card><CardContent>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {summary.total_calls > 0 ? Math.round(summary.total_calls / summary.period_days * 10) / 10 : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>Calls/Day Avg</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          {/* Response times measured from per-unit time punches, so a
              super-admin correction is reflected here. */}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Unit Response Times
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            From unit time punches on {summary.calls_with_punches} call(s)
            {summary.calls_with_corrected_times > 0
              ? ` — ${summary.calls_with_corrected_times} include corrected times`
              : ''}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Avg Turnout (dispatch → en route)', value: summary.avg_turnout_seconds },
              { label: 'Avg Travel (en route → on scene)', value: summary.avg_travel_seconds },
              { label: 'Avg Call → On Scene', value: summary.avg_to_scene_seconds },
              { label: 'Avg Time On Scene', value: summary.avg_scene_seconds },
            ].map((m) => (
              <Grid size={{ xs: 6, md: 3 }} key={m.label}>
                <Card><CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
                    {formatDuration(m.value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {m.label}
                  </Typography>
                </CardContent></Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>By Call Type</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Code</TableCell><TableCell>Type</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {callTypeStats.map((ct) => (
                      <TableRow key={ct.code}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{ct.code}</TableCell>
                        <TableCell>{ct.name}</TableCell>
                        <TableCell align="right">{ct.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>By Priority</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Priority</TableCell><TableCell>Label</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {Object.entries(summary.by_priority).map(([p, count]) => (
                      <TableRow key={p}>
                        <TableCell sx={{ fontWeight: 700 }}>P{p}</TableCell>
                        <TableCell>{PRIORITY_LABELS[p] || p}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>By Agency</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Agency</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {agencyStats.map((a) => (
                      <TableRow key={a.agency}>
                        <TableCell>{a.agency}</TableCell>
                        <TableCell align="right">{a.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>By Status</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Status</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {Object.entries(summary.by_status).map(([s, count]) => (
                      <TableRow key={s}>
                        <TableCell>{s}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>By Disposition</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Disposition</TableCell><TableCell align="right">Count</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {Object.entries(summary.by_disposition).map(([d, count]) => (
                      <TableRow key={d}>
                        <TableCell>{d}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                    {Object.keys(summary.by_disposition).length === 0 && (
                      <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', color: 'text.secondary' }}>No dispositions recorded</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Daily Call Volume</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>Date</TableCell><TableCell align="right">Calls</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {Object.entries(summary.daily_counts).map(([date, count]) => (
                      <TableRow key={date}>
                        <TableCell>{date}</TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
